import { subscriptionsApi } from '@/lib/api';
import type { Subscription } from '@/lib/api/services/subscriptions';
import { isSubscriptionActive } from '@/lib/subscription-utils';

/**
 * Wait until the BACKEND reports the state a payment was meant to produce.
 *
 * Post-payment code used to reload the page on a fixed timer (1.5s after
 * verify, or immediately on UPI dismiss) and hope the server had caught up. If
 * activation was still in flight — webhook-only paths, a slow gateway read, a
 * UPI payment settling on the phone — the reload landed on the old state and a
 * customer who had just paid saw the expired gate again, with nothing
 * retrying. Now the client asks the server (`/subscriptions/current`, the same
 * resolver the API gate uses) and only moves on once it says so.
 *
 * @returns true once `until(subscription)` holds, false on timeout.
 */
export async function awaitSubscriptionAccess({
  until = (sub: Subscription) => isSubscriptionActive(sub),
  timeoutMs = 45_000,
  intervalMs = 1_500,
}: {
  until?: (sub: Subscription) => boolean;
  timeoutMs?: number;
  intervalMs?: number;
} = {}): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    try {
      const res = await subscriptionsApi.getCurrentSubscription();
      const sub = res?.data as Subscription | null | undefined;
      if (sub && until(sub)) return true;
    } catch {
      /* transient — keep polling until the deadline */
    }
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}
